/**
 * Lab-related TypeScript types
 */

export enum RunState {
  PENDING = 'pending',
  PROVISIONING = 'provisioning',
  ACTIVE = 'active',
  DESTROYING = 'destroying',
  DESTROYED = 'destroyed',
  FAILED = 'failed'
}

export enum Difficulty {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced'
}

export interface LabSummary {
  id: number;
  slug: string;
  title: string;
  summary?: string;
  difficulty: Difficulty;
  estimated_minutes: number;
  tags: string[];
}

export interface Lab extends LabSummary {
  os: string;
  droplet_size?: string;
  prerequisites?: string[];
  manifest: {
    version?: string;
    description?: string;
    instructions?: string;
    rubric: RubricItem[];
    [key: string]: any;
  };
  created_at: string;
  updated_at: string;
}

export interface RubricItem {
  id: string;
  description: string;
  points: number;
  check_type: string;
  check_config: Record<string, any>;
}

export interface RunInstance {
  id: number;
  name: string;
  role: string;
  is_primary: boolean;
  public_ip: string | null;
  private_ip: string | null;
  droplet_id: string | null;
  droplet_size: string | null;
  ssh_command: string | null;
  provisioned: boolean;
}

export interface Run {
  id: number;
  lab_id: string;
  user_id: number;
  state: RunState;
  droplet_id?: string;
  ip_address?: string;
  ttl_expires_at: string;
  created_at: string;
  updated_at: string;
  instances?: RunInstance[];
  lab?: Lab;
  user?: {
    username: string;
    email: string;
  };
}

export interface RunCreate {
  lab_id: string;
  // Optional Spaces integration (used by Spaces-dependent labs)
  spaces_credential_id?: number;
  spaces_bucket?: string;
  spaces_prefix?: string;
}

export interface RunExtend {
  hours: number;
}

export interface Grade {
  id: number;
  run_id: number;
  score: number;
  total_points: number;
  report: GradeReport;
  created_at: string;
  override?: GradeOverride;
}

export interface GradeReport {
  checks: CheckResult[];
  summary: {
    passed_checks: number;
    failed_checks: number;
    total_checks: number;
    score: number;
    total_points: number;
    percentage: number;
    execution_time: number;
  };
  execution_time: number;
  timestamp?: string;
  metadata?: {
    graded_at: string;
    grading_version: string;
    ssh_connection_time?: number;
    average_check_time?: number;
  };
}

export interface CheckResult {
  id: string;
  description: string;
  passed: boolean;
  points_earned: number;
  total_points: number;
  actual_value?: string;
  expected_value?: string;
  error_message?: string;
  execution_time: number;
  metadata?: any;
}

export interface GradeOverride {
  id: number;
  grade_id: number;
  instructor_id: number;
  new_score: number;
  comment: string;
  created_at: string;
  instructor?: {
    username: string;
  };
}
